/**
 * The typed HTTP client of contracts/api.ts — the web implementation of `ApiCaller` (contracts/wiring.ts).
 * Used on the main thread (sign-in, billing, connectors) and inside the engine worker (sync, relay, IMAP
 * bridge, scoring proxy).
 *
 *   - `credentials: 'include'`: the session is the httpOnly cookie on the API host (api.md "Auth").
 *   - Every request carries `X-Digester-Client: web` (the CSRF guard of api.md "Conventions").
 *   - Signed out, the install token from local settings goes out as `X-Digester-Install`; one is
 *     requested from `POST /api/install` the first time a route that accepts it is called.
 *   - Non-2xx answers become `DigesterError`s from the `ApiErrorBody`; an unreachable server or a
 *     timeout becomes `fetch_failed`.
 */

import { API, DigesterError, isErrorCode, type ApiCaller, type ApiRoute, type ErrorCode, type ErrorDetails, type InstallResponse } from '@digester/core';

/** Where the caller reads (and stores) its anonymous credentials. */
export interface ApiAuthState {
  /** True when an account is signed in on this device (the cookie then authenticates). */
  signedIn(): Promise<boolean>;
  installToken(): Promise<string | null>;
  setInstallToken(token: string): Promise<void>;
}

export interface ApiCallerOptions {
  /** API origin without a trailing slash; '' = this page's origin (dev: the Vite proxy). */
  base: string;
  /** Default: the global fetch. */
  fetch?: typeof fetch;
  /** Value of X-Digester-Client. Default 'web'. */
  client?: string;
  /** Absent: never send an install token. */
  auth?: ApiAuthState;
  /** Per-request timeout. Default 30 s (sync pages can be large). */
  timeoutMs?: number;
}

export interface WebApiCaller extends ApiCaller {
  /** The absolute URL of a route (for links and for tests). */
  url(route: ApiRoute, params?: Record<string, string>, query?: Record<string, string | number>): string;
}

/** Routes that accept the anonymous install token (api.md "Relay", "IMAP bridge"). */
const INSTALL_ROUTES: ReadonlySet<ApiRoute> = new Set<ApiRoute>(['relay', 'imapMailboxes', 'imapFetch']);

const STATUS_CODES: ReadonlyArray<readonly [number, ErrorCode]> = [
  [400, 'invalid_input'], [401, 'unauthenticated'], [402, 'plan_limit'], [403, 'forbidden'], [404, 'not_found'],
  [409, 'conflict'], [410, 'resync_required'], [422, 'fetch_blocked'], [429, 'rate_limited'], [502, 'fetch_failed'],
  [503, 'scoring_unavailable'], [504, 'fetch_failed'],
];

function codeForStatus(status: number): ErrorCode {
  return STATUS_CODES.find(([s]) => s === status)?.[1] ?? 'internal';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Turn a non-2xx response into a DigesterError (ApiErrorBody when present, else the status mapping). */
export function errorFromResponse(status: number, body: unknown, retryAfter: string | null): DigesterError {
  const error = isRecord(body) && isRecord(body.error) ? body.error : null;
  const code: ErrorCode = error && isErrorCode(error.code) ? error.code : codeForStatus(status);
  const message = error && typeof error.message === 'string' ? error.message : `The server answered ${status}`;
  const details: ErrorDetails = error && isRecord(error.details) ? { ...(error.details as ErrorDetails) } : {};
  if (details.status === undefined) details.status = status;
  if (code === 'rate_limited' && details.retryAfterMs === undefined && retryAfter !== null) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) details.retryAfterMs = Math.round(seconds * 1000);
  }
  return new DigesterError(code, message, details);
}

function splitRoute(route: ApiRoute): { method: string; path: string } {
  const spec = API[route];
  const space = spec.indexOf(' ');
  return { method: spec.slice(0, space), path: spec.slice(space + 1) };
}

function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new DigesterError('invalid_input', `Missing route parameter ${name}`);
    return encodeURIComponent(value);
  });
}

function queryString(query: Record<string, string | number> | undefined): string {
  if (!query) return '';
  const pairs = Object.entries(query).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function createApiCaller(options: ApiCallerOptions): WebApiCaller {
  const base = options.base.replace(/\/+$/, '');
  const doFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init));
  const client = options.client ?? 'web';
  const timeoutMs = options.timeoutMs ?? 30_000;
  let installing: Promise<string | null> | null = null;

  const url: WebApiCaller['url'] = (route, params, query) => `${base}${fillPath(splitRoute(route).path, params)}${queryString(query)}`;

  /** The install token, created on first need (≤ 5 per IP per day on the server, so it is kept). */
  const installToken = async (): Promise<string | null> => {
    const auth = options.auth;
    if (!auth) return null;
    const stored = await auth.installToken();
    if (stored) return stored;
    installing ??= (async () => {
      try {
        const res = await send<InstallResponse>('install', {});
        if (!res || typeof res.token !== 'string') return null;
        await auth.setInstallToken(res.token);
        return res.token;
      } finally {
        installing = null;
      }
    })();
    return installing;
  };

  async function send<Res>(route: ApiRoute, call: Parameters<ApiCaller['call']>[1], retried = false): Promise<Res> {
    const { method } = splitRoute(route);
    const headers: Record<string, string> = { Accept: 'application/json', 'X-Digester-Client': client };
    let body: string | undefined;
    if (call?.body !== undefined && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(call.body);
    }
    let usedInstall = false;
    if (route !== 'install' && options.auth && INSTALL_ROUTES.has(route) && !(await options.auth.signedIn())) {
      const token = await installToken();
      if (token) {
        headers['X-Digester-Install'] = token;
        usedInstall = true;
      }
    }
    Object.assign(headers, call?.headers ?? {});
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      const init: RequestInit = { method, headers, credentials: 'include', signal: controller.signal };
      if (body !== undefined) init.body = body;
      response = await doFetch(url(route, call?.params, call?.query), init);
    } catch (error) {
      const timedOut = controller.signal.aborted;
      throw new DigesterError('fetch_failed', timedOut ? 'The server did not answer in time.' : 'The server is unreachable.', {
        reason: timedOut ? 'timeout' : 'network',
      });
    } finally {
      clearTimeout(timer);
    }
    const parsed = await readBody(response);
    if (!response.ok) {
      // An install token unused for weeks expires on the server: forget it, get a new one, retry once.
      if (response.status === 401 && usedInstall && !retried && options.auth) {
        await options.auth.setInstallToken('');
        return send<Res>(route, call, true);
      }
      throw errorFromResponse(response.status, parsed, response.headers.get('retry-after'));
    }
    return parsed as Res;
  }

  return {
    url,
    call: send,
  };
}
